/**
 * Weergave **per gebouw** van de helpdeskticketlijst.
 *
 * Twee groeperingsniveaus: gebouw (open-/dichtklikbaar) en daarbinnen unit.
 * Rijen, pickers, bulkacties en kolomgedrag komen uit dezelfde UniversalTable
 * als de vlakke lijst — enkel de standaardsortering verschilt, want gebouw en
 * unit staan al in de koppen.
 *
 * Groeperen, sorteren en de tellers gebeuren backend-side; zie
 * `useHelpdeskTicketGroups` en `GET /helpdesk/tickets/grouped`.
 *
 * @module Helpdesk/Components/HelpdeskBuildingGroupedTable
 */

import { Warning } from "@phosphor-icons/react";
import { UniversalTable } from "shared/components/UniversalTable";
import type { BulkAction } from "shared/components/filterBar/types";
import type { CellConfig } from "shared/components/UniversalTable/components";
import { HELPDESK_BUILDING_TABLE_CONFIG } from "../configs/helpdeskTableConfig";
import type {
  HelpdeskBuildingGroup,
  HelpdeskUnitGroup,
} from "../shared/hooks/useHelpdeskTicketGroups";
import {
  HelpdeskBuildingGroupHeader,
  HelpdeskUnitGroupHeader,
} from "./HelpdeskGroupHeaders";

interface HelpdeskBuildingGroupedTableProps {
  rows: any[];
  /** Gebouwmetadata per groepeersleutel (voor de tellers in de koppen). */
  groupsByKey: Map<string, HelpdeskBuildingGroup>;
  /** Unitmetadata per groepeersleutel. */
  unitsByKey: Map<string, HelpdeskUnitGroup>;
  isLoading: boolean;
  selectedRows: Set<number>;
  onSelectionChange: (rows: Set<number>) => void;
  renderRow: (ticket: any, context: any) => CellConfig[];
  bulkActions: BulkAction[];
  onUpdate: (ticketId: number, updates: any) => void;
  columnActions?: any;
  onModalRequest?: (event: any) => void;
  onComplete?: (ticketId: number) => Promise<void>;
  onMoveToCompletion?: (ticketId: number) => Promise<void>;
  onRevertToOpen?: (ticketId: number) => Promise<void>;
  /** true wanneer het ticketplafond bereikt is en de gebruiker moet filteren. */
  truncated: boolean;
  loaded: number;
  total: number;
}

export const HelpdeskBuildingGroupedTable = ({
  rows,
  groupsByKey,
  unitsByKey,
  isLoading,
  selectedRows,
  onSelectionChange,
  renderRow,
  bulkActions,
  onUpdate,
  columnActions,
  onModalRequest,
  onComplete,
  onMoveToCompletion,
  onRevertToOpen,
  truncated,
  loaded,
  total,
}: HelpdeskBuildingGroupedTableProps) => (
  <>
    {truncated && (
      <div className="mx-6 mb-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <Warning className="h-4 w-4 shrink-0" weight="duotone" />
        <span>
          {loaded} van {total} tickets geladen. De tellers per gebouw en unit zijn
          volledig; verfijn je filters om alle tickets in de lijst te zien.
        </span>
      </div>
    )}

    {/* De gegroepeerde weergave rendert enkel accordeongroepen; zonder rijen is
        er dus niets te zien. Vandaar hier een eigen lege/laad-status. */}
    {rows.length === 0 && (
      <div className="mx-6 mb-2 rounded-md border border-gray-200 bg-white px-4 py-10 text-center">
        <p className="text-neutral-primary text-sm font-medium">
          {isLoading ? "Tickets laden..." : "Geen tickets gevonden"}
        </p>
        {!isLoading && (
          <p className="text-neutral-secondary-mid mt-1 text-xs">
            Pas je filters aan of maak een nieuw ticket aan.
          </p>
        )}
      </div>
    )}

    <UniversalTable
      columnsConfig={HELPDESK_BUILDING_TABLE_CONFIG.columnsConfig}
      pickerConfig={HELPDESK_BUILDING_TABLE_CONFIG.pickerConfig}
      sortConfig={HELPDESK_BUILDING_TABLE_CONFIG.sortConfig}
      rows={rows}
      groupBy="buildingGroupKey"
      subGroupBy="unitGroupKey"
      allowMultipleGroupsOpen
      renderGroupHeader={(groupKey, groupRows) => (
        <HelpdeskBuildingGroupHeader
          group={groupsByKey.get(groupKey)}
          fallbackCount={groupRows.length}
        />
      )}
      renderSubGroupHeader={(subGroupKey, subGroupRows) => (
        <HelpdeskUnitGroupHeader
          unit={unitsByKey.get(subGroupKey)}
          fallbackCount={subGroupRows.length}
        />
      )}
      title=""
      isLoading={isLoading}
      renderRow={renderRow}
      selectedRows={selectedRows}
      onSelectionChange={onSelectionChange}
      searchConfig={{ bulkActions }}
      columnActions={columnActions}
      onModalRequest={onModalRequest}
      onUpdate={onUpdate}
      onComplete={onComplete}
      onMoveToCompletion={onMoveToCompletion}
      onRevertToOpen={onRevertToOpen}
    />
  </>
);
